import { Alert, Box, Typography } from '@mui/material';
import { Field, FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

import RareButton from '../../components/common/buttons/RareButton';
import NotificationSnackbarCard from '../../components/common/cards/NotificationSnackbarCard';
import { TextEditField } from '../../components/common/fields/fields';
import colorConfigs from '../../configs/colorConfigs';
import api from '../../redux/features/auth/axiosAuthHelper';
import { RootState } from '../../redux/store';

interface EncryptionKey {
  key: string;
  value: string;
}

const ENCRYPTION_KEY = 'encryption-key';

const KeysPage = () => {
  const authData = useSelector((state: RootState) => state.auth);
  const [keyData, setKeyData] = useState<EncryptionKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorQueue, setErrorQueue] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    const fetchKey = async () => {
      try {
        const response = await api.get('data-mappings/encryption-key/', {
          headers: { Authorization: `Bearer ${authData.access}` },
        });
        if (!current) return;
        setKeyData(response.data);
        setError(null);
      } catch {
        if (!current) return;
        setError('No key found');
      }
    };
    fetchKey();
    return () => { current = false; };
  }, [authData.access]);

  const formik = useFormik<EncryptionKey>({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: keyData ?? { key: ENCRYPTION_KEY, value: '' },
    validationSchema: Yup.object({
      key: Yup.string().required('This field is required'),
      value: Yup.string().required('This field is required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      setErrorQueue([]);
      setSuccessMessage(null);

      const url = keyData ? `data-mappings/${values.key}/` : 'data-mappings/';
      const method = keyData ? 'patch' : 'post';

      try {
        await api({
          method,
          url,
          data: authData.isPortalUser ? { key: values.key, value: values.value } : {},
          headers: { Authorization: `Bearer ${authData.access}` },
        });
        if (!keyData) setKeyData(values);
        setError(null);
        setSuccessMessage('Encryption Key successfully updated');
        resetForm({ values });
      } catch (requestError: any) {
        let message = 'Unknown error updating Encryption Key';
        const status = requestError?.response?.status;
        const responseData = requestError?.response?.data;
        if ((status === 400 || status === 403) && responseData && typeof responseData === 'object') {
          const firstField = Object.keys(responseData)[0];
          if (firstField) {
            message = status === 400
              ? `${firstField}: ${responseData[firstField]}`
              : String(responseData[firstField]);
          }
        }
        if (status === 403 && message === 'Unknown error updating Encryption Key') {
          message = 'You do not have permission to update the Encryption Key.';
        }
        setError(message);
        setErrorQueue([message]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
  });

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, minHeight: '100%', boxSizing: 'border-box' }}>
      <Typography
        component="h1"
        align="center"
        fontWeight={700}
        fontSize={28}
        color={colorConfigs.tables.headBg}
        sx={{ pt: 1.5, mb: 2, px: 7 }}
      >
        Encryption Key
      </Typography>

      <FormikProvider value={formik}>
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{
            width: '100%',
            maxWidth: 640,
            mx: 'auto',
            mt: { xs: 2, md: 4 },
            p: { xs: 2, sm: 3 },
            boxSizing: 'border-box',
            bgcolor: 'background.paper',
            borderRadius: 2,
          }}
        >
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Field name="value" type="text" label="Value" component={TextEditField} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              borderTop: '1px solid #e1e8e6',
              mt: 3,
              pt: 2,
            }}
          >
            <RareButton type="submit" variant="success" disabled={!formik.dirty || formik.isSubmitting}>
              Save Changes
            </RareButton>
          </Box>
        </Box>
      </FormikProvider>

      <NotificationSnackbarCard
        errorQueue={errorQueue}
        successMessage={successMessage}
        setErrorQueue={setErrorQueue}
        setSuccessMessage={setSuccessMessage}
      />
    </Box>
  );
};

export default KeysPage;
